import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MEDIA_MOBILE_NAV_CLOSE,
  MEDIA_MOBILE_NAV_SYNC,
  MEDIA_MOBILE_NAV_TOGGLE,
  type MediaMobileNavSyncDetail,
} from "../utils/mediaMobileNavEvents";
import { samePageHashHref, scrollToElementByHash } from "../utils/scrollToHash";
import "./MobileNavDock.css";

const LEGACY_AUTHOR_PATH = /^\/author(\/|$)/;
const MENU_WAVE = "/images/xrp_sydney_2027_assets/waves1.svg";
/** Keep in sync with sheet close animation duration in MobileNavDock.css */
const MENU_CLOSE_MS = 280;

type MenuSubLink = { label: string; to?: string };

type MenuGroup = {
  title: string;
  to: string;
  paths: string[];
  links: MenuSubLink[];
};

/** Priority order: primary destinations first, supporting / archival last */
const menuGroups: MenuGroup[] = [
  {
    title: "2027 Events",
    to: "/xrp-australia",
    paths: ["/xrp-australia", "/xrp-london"],
    links: [
      { label: "Sydney", to: "/xrp-australia" },
      { label: "London", to: "/xrp-london" },
    ],
  },
  {
    title: "About",
    to: "/about",
    paths: ["/about", "/about-us"],
    links: [
      { label: "Home", to: "/" },
      { label: "Our Mission", to: "/about#our-mission" },
      { label: "Past events", to: "/events" },
    ],
  },
  /* SHELVED PROGRAMS
  {
    title: "Programs",
    to: "/programs",
    paths: ["/programs", "/register"],
    links: [
      { label: "Institutions", to: "/programs" },
      { label: "Partners", to: "/programs#footer-register" },
    ],
  },
  */
  {
    title: "Company",
    to: "/team",
    paths: ["/team", "/careers"],
    links: [
      { label: "Team", to: "/team" },
      { label: "Careers", to: "/careers" },
    ],
  },
  {
    title: "Resources",
    to: "/resources",
    paths: ["/resources", "/blog", "/gallery", "/gallery_resources", "/branding", "/xrp-australia-2026-collage", "/faq"],
    links: [
      { label: "Blog", to: "/blog" },
      /* SHELVED MEDIA { label: "Media", to: "/media" }, */
      { label: "FAQ", to: "/faq" },
      { label: "Gallery", to: "/gallery" },
      { label: "Brand assets", to: "/branding" },
    ],
  },
];

function isPathActive(pathname: string, paths: string[]) {
  return paths.some((path) => pathname === path || pathname.startsWith(path + "/"));
}

function isSubLinkActive(pathname: string, to: string) {
  const [pathOnly] = to.split("#");
  if (pathOnly === "/") {
    return pathname === "/";
  }
  if (to === "/blog") {
    return pathname === "/blog" || pathname.startsWith("/blog/");
  }
  return pathname === pathOnly || pathname.startsWith(pathOnly + "/");
}

function useNarrowForMobileNav(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const MobileNavDock: React.FC = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMounted, setMenuMounted] = useState(false);
  const narrow = useNarrowForMobileNav();

  const { pathname } = location;
  const isMediaPath = pathname === "/media" || pathname.startsWith("/media/");
  const isMediaMobile = isMediaPath && narrow;

  const closeMenu = useCallback(() => {
    if (isMediaMobile) {
      window.dispatchEvent(new CustomEvent(MEDIA_MOBILE_NAV_CLOSE));
      return;
    }
    setMenuOpen(false);
  }, [isMediaMobile]);

  const openMenu = useCallback(() => {
    setMenuMounted(true);
    setMenuOpen(true);
  }, []);

  useEffect(() => {
    if (isMediaMobile) {
      closeMenu();
      return;
    }
    closeMenu();
  }, [location.pathname, location.hash, narrow, isMediaMobile, closeMenu]);

  useEffect(() => {
    const onSync = (e: Event) => {
      const ce = e as CustomEvent<MediaMobileNavSyncDetail>;
      if (typeof ce.detail?.open === "boolean") {
        if (ce.detail.open) {
          setMenuMounted(true);
          setMenuOpen(true);
        } else {
          setMenuOpen(false);
        }
      }
    };
    window.addEventListener(MEDIA_MOBILE_NAV_SYNC, onSync as EventListener);
    return () => window.removeEventListener(MEDIA_MOBILE_NAV_SYNC, onSync as EventListener);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (!menuMounted) return;
    if (isMediaMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuMounted, isMediaMobile]);

  // Unmount after close animation (fallback if animationend is skipped)
  useEffect(() => {
    if (menuOpen || !menuMounted || isMediaMobile) return;
    if (prefersReducedMotion()) {
      setMenuMounted(false);
      return;
    }
    const id = window.setTimeout(() => setMenuMounted(false), MENU_CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [menuOpen, menuMounted, isMediaMobile]);

  if (LEGACY_AUTHOR_PATH.test(location.pathname)) {
    return null;
  }

  const onFabClick = () => {
    if (isMediaMobile) {
      window.dispatchEvent(new CustomEvent(MEDIA_MOBILE_NAV_TOGGLE));
      return;
    }
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const fabAriaControls = isMediaMobile
    ? "media-mobile-nav-sheet"
    : menuMounted
      ? "mobile-fullscreen-menu"
      : undefined;

  return (
    <>
      <button
        type="button"
        className={`mobile-menu-fab${menuOpen ? " is-open" : ""}`}
        aria-label={
          isMediaMobile
            ? menuOpen
              ? "Close media menu"
              : "Open media menu"
            : menuOpen
              ? "Close mobile menu"
              : "Open mobile menu"
        }
        aria-expanded={menuOpen}
        aria-controls={fabAriaControls}
        onClick={onFabClick}
      >
        <span className="mobile-menu-fab__burger" aria-hidden="true">
          <span className="mobile-menu-fab__bar" />
          <span className="mobile-menu-fab__bar" />
          <span className="mobile-menu-fab__bar" />
        </span>
      </button>

      {menuMounted && !isMediaMobile ? (
        <div
          id="mobile-fullscreen-menu"
          className={`mobile-fullscreen-menu${menuOpen ? " is-open" : " is-closing"}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile menu"
          onAnimationEnd={(e) => {
            if (e.target !== e.currentTarget) return;
            if (!menuOpen) setMenuMounted(false);
          }}
        >
          <div className="mobile-fullscreen-menu__wave" aria-hidden>
            <img
              src={MENU_WAVE}
              alt=""
              className="mobile-fullscreen-menu__wave-img"
              width={3750}
              height={975}
              decoding="async"
            />
          </div>

          <div className="mobile-fullscreen-menu__scroll">
            <section className="mobile-menu-section">
              <header className="mobile-menu-intro">
                <Link to="/" className="mobile-menu-logo-link" onClick={closeMenu}>
                  <img
                    src="/woi_logos/woi+X_logo_light_bg.svg"
                    alt="Wave of Innovation"
                    className="mobile-menu-logo"
                    width={3750}
                    height={975}
                    decoding="async"
                  />
                </Link>
              </header>

              <nav className="mobile-menu-nav" aria-label="Site">
                {menuGroups.map((group, index) => {
                  const groupActive = isPathActive(pathname, group.paths);
                  return (
                    <div
                      key={group.title}
                      className="mobile-menu-group"
                      style={{ "--menu-group-i": index } as React.CSSProperties}
                    >
                      <Link
                        to={group.to}
                        className={`mobile-menu-group__heading${groupActive ? " is-active" : ""}`}
                        aria-current={groupActive ? "page" : undefined}
                        onClick={closeMenu}
                      >
                        <span className="mobile-menu-group__kicker">{group.title}</span>
                      </Link>
                      <ul className="mobile-menu-group__list">
                        {group.links.map((item) => (
                          <li key={item.label}>
                            {item.to ? (
                              <Link
                                to={item.to}
                                className={`mobile-menu-group__link${isSubLinkActive(pathname, item.to) ? " is-active" : ""}`}
                                aria-current={isSubLinkActive(pathname, item.to) ? "page" : undefined}
                                onClick={closeMenu}
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <span className="mobile-menu-group__text">{item.label}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </nav>

              <div className="mobile-menu-cta">
                <a
                  href={samePageHashHref(pathname, "#footer-register")}
                  className="mobile-menu-register"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMenu();
                    // Let the sheet start closing before scrolling the page underneath.
                    window.setTimeout(() => scrollToElementByHash("#footer-register"), 120);
                  }}
                >
                  Register now
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            </section>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default MobileNavDock;
